import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  Box,
  TextField,
  Autocomplete,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Avatar,
  IconButton,
  Grid,
  Fab,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  PhoneRounded as PhoneIcon,
  EmailRounded as EmailIcon,
  AddRounded as AddIcon,
  SearchRounded as SearchIcon,
  ContentCopyRounded as CopyIcon,
  MailRounded as MailIcon,
} from '@mui/icons-material';
import { GET_PERSONS } from '../apollo/queries';
import { getDisplayName, getInitials, calculateAge, getPersonAvatarUrl } from '../hooks/usePersonHelpers';
import PersonFormDialog from '../components/PersonFormDialog';

function DirectoryPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const { data, loading, error } = useQuery(GET_PERSONS);

  const filteredPersons = useMemo(() => {
    if (!data?.persons) return [];
    if (!searchTerm) return data.persons;

    const term = searchTerm.toLowerCase();
    return data.persons.filter((person) => {
      const fullName = `${person.firstName} ${person.lastName}`.toLowerCase();
      const preferred = (person.preferredName || '').toLowerCase();
      return fullName.includes(term) || preferred.includes(term);
    });
  }, [data?.persons, searchTerm]);

  // Get all persons with emails (exclude deceased)
  const personsWithEmails = useMemo(() => {
    if (!data?.persons) return [];
    return data.persons.filter((p) => p.email && !p.deceasedDate);
  }, [data?.persons]);

  const handleCopyEmails = () => {
    // Format as "Name <email>" for better email client compatibility
    const emailString = personsWithEmails
      .map((p) => `${getDisplayName(p)} <${p.email}>`)
      .join(', ');
    navigator.clipboard.writeText(emailString).then(() => {
      setSnackbarOpen(true);
    });
  };

  const handleEmailEveryone = () => {
    // Use BCC for privacy - each person won't see others' emails
    const emails = personsWithEmails.map((p) => p.email);
    const mailtoLink = `mailto:?bcc=${emails.join(',')}`;
    window.location.href = mailtoLink;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading directory: {error.message}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" fontWeight={600}>
          Family Directory
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {personsWithEmails.length > 0 && (
            <>
              <Tooltip title={`Copy ${personsWithEmails.length} email addresses`}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={handleCopyEmails}
                >
                  Copy Emails
                </Button>
              </Tooltip>
              <Tooltip title={`Open mail client with ${personsWithEmails.length} recipients in BCC`}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MailIcon />}
                  onClick={handleEmailEveryone}
                >
                  Email Everyone
                </Button>
              </Tooltip>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddDialog(true)}
          >
            Add Person
          </Button>
        </Box>
      </Box>

      <Autocomplete
        freeSolo
        options={data?.persons || []}
        getOptionLabel={(option) => 
          typeof option === 'string' ? option : getDisplayName(option)
        }
        onInputChange={(_, value) => setSearchTerm(value)}
        onChange={(_, value) => {
          if (value && typeof value !== 'string') {
            navigate(`/person/${value.id}`);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search by name..."
            InputProps={{
              ...params.InputProps,
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
          />
        )}
        renderOption={(props, person) => (
          <Box component="li" {...props} key={person.id}>
            <Avatar
              src={person.profilePictureUrl}
              sx={{ mr: 2, width: 32, height: 32 }}
            >
              {getInitials(person)}
            </Avatar>
            {getDisplayName(person)}
          </Box>
        )}
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filteredPersons.length} family member{filteredPersons.length !== 1 ? 's' : ''}
      </Typography>

      <Grid container spacing={2}>
        {filteredPersons.map((person) => (
          <Grid item xs={12} sm={6} md={4} key={person.id}>
            <Card sx={{ height: '100%' }}>
              <CardActionArea
                onClick={() => navigate(`/person/${person.id}`)}
                sx={{ height: '100%' }}
              >
                <CardContent sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  height: '100%',
                  boxSizing: 'border-box',
                }}>
                  <Avatar
                    src={getPersonAvatarUrl(person, 56)}
                    alt={`Photo of ${getDisplayName(person)}`}
                    sx={{ width: 56, height: 56 }}
                  >
                    {getInitials(person)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={500} noWrap>
                      {getDisplayName(person)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ minHeight: 24 }}>
                      {person.birthday ? (
                        <>
                          Age: {calculateAge(person.birthday, person.deceasedDate)}
                          {person.deceasedDate && (
                            <Chip label="Deceased" size="small" sx={{ ml: 1 }} />
                          )}
                        </>
                      ) : (
                        '\u00A0' // Non-breaking space to maintain height
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', ml: 'auto' }}>
                    <IconButton
                      component="a"
                      href={person.phone ? `tel:${person.phone}` : undefined}
                      onClick={(e) => {
                        if (person.phone) {
                          e.stopPropagation();
                        } else {
                          e.preventDefault();
                        }
                      }}
                      size="small"
                      color={person.phone ? 'primary' : 'default'}
                      disabled={!person.phone}
                      sx={{ opacity: person.phone ? 1 : 0.3 }}
                    >
                      <PhoneIcon />
                    </IconButton>
                    <IconButton
                      component="a"
                      href={person.email ? `mailto:${person.email}` : undefined}
                      onClick={(e) => {
                        if (person.email) {
                          e.stopPropagation();
                        } else {
                          e.preventDefault();
                        }
                      }}
                      size="small"
                      color={person.email ? 'primary' : 'default'}
                      disabled={!person.email}
                      sx={{ opacity: person.email ? 1 : 0.3 }}
                    >
                      <EmailIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Fab
        color="primary"
        aria-label="Add family member"
        onClick={() => setShowAddDialog(true)}
        sx={{ position: 'fixed', bottom: { xs: 72, md: 24 }, right: 24 }}
      >
        <AddIcon />
      </Fab>

      <PersonFormDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={`${personsWithEmails.length} email addresses copied to clipboard`}
      />
    </Box>
  );
}

export default DirectoryPage;

